import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useTimer } from 'react-timer-hook';
import RedMoneySVG from '../../public/img/redMoney.svg';
import WhenSVG from '../../public/img/when.svg';
import LocationSVG from '../../public/img/location.svg';
import ViewsSVG from '../../public/img/views2.svg';
import RatingSVG from '../../public/img/star-outline.svg';
import PushkinSVG from '../../public/img/pushkin.svg';
import { getTitleForTimer } from '../../utils/getTitleForTimer';
import { eventTypes } from '../../constants/events';
import {
  ActionLabelItem,
  DescriptionItem,
  LabelFeatureListItem,
} from './styled';
import { Picture } from '../picture/picture';
import { Link } from '../styled/Link';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { Action } from '../../models';
import FavoriteButton from '../favorite/FavoriteButton';
import FavoriteButtonCardWrapper from '../favorite/FavoriteButtonCardWrapper';
import { FavoriteKinds } from '../../models/favorite';
import { ImageSources } from '../../models/image';
import { getPriceWithCurrency } from '../../utils/getPriceWithCurrency';
import { splitPriceByThree } from '../../utils/splitPriceByThree';
import { countdownInit } from '../../utils/countdownInit';
import { formatTimer } from '../../utils/formatTimer';
import { getBenefit } from '../../utils/getBenefit';

type ITimerData = {
  showTimer: boolean;
  startDate: Date | null;
  endDate: Date | null;
  title: string | null;
};

export function PostCard({
  item,
  catalog,
  scroll,
  withOutWrapper,
  href,
  showOnlyCityInAddress = false,
}: {
  item: Action;
  catalog?: boolean;
  scroll?: boolean;
  withOutWrapper?: boolean;
  href?: string | null;
  showOnlyCityInAddress?: boolean;
}) {
  const theme = useTheme();

  const [timerData, setTimerData] = useState<ITimerData>({
    showTimer: false,
    startDate: null,
    endDate: null,
    title: null,
  });

  const { seconds, minutes, hours, days, restart } = useTimer({
    expiryTimestamp: timerData.endDate
      ? DateTime.fromJSDate(timerData.endDate, { zone: 'utc' }).toJSDate()
      : new Date(),
    onExpire: () => console.warn('onExpire called'),
  });

  const [progressValue, setProgressValue] = useState(0);

  useEffect(() => {
    const newTimerData: ITimerData = {
      showTimer: false,
      startDate: null,
      endDate: null,
      title: getTitleForTimer(item.countdown_type),
    };

    const dataPrepareForTimer = countdownInit(
      [DateTime.fromISO(item.countdown_datetime)],
      item.countdown_type
    );

    if (dataPrepareForTimer) {
      newTimerData.showTimer = true;
      newTimerData.endDate = new Date(Date.now() + dataPrepareForTimer.date);
      newTimerData.startDate = DateTime.fromMillis(
        Date.now() + dataPrepareForTimer.date
      )
        .minus({
          weeks: 1,
        })
        .toJSDate();
    }

    if (newTimerData.showTimer) {
      const a =
        Number(Date.now() - Number(newTimerData.startDate?.getTime())) * 100;
      const b =
        Number(newTimerData.endDate?.getTime()) -
        Number(newTimerData.startDate?.getTime());
      const newProgressVal = Math.round(a / b);
      setProgressValue(newProgressVal);

      if (newTimerData.endDate) {
        restart(newTimerData.endDate);
      }
    }

    setTimerData(newTimerData);
  }, [item]);

  return withOutWrapper ? (
    <div
      // action-card
      itemScope
      itemType='https://schema.org/Event'
      css={{
        height: '100%',
        borderRadius: '4px',
        backgroundColor: '#fff',
        position: 'relative',
        top: 0,
        width: '100%',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.constants.boxShadow,
        },
      }}
    >
      {item.nearest_event && item.nearest_event.start_date && (
        <meta itemProp='startDate' content={item.nearest_event.start_date} />
      )}
      {item.nearest_event && item.nearest_event.end_date && (
        <meta itemProp='startDate' content={item.nearest_event.end_date} />
      )}
      <meta itemProp='name' content={item.title} />
      <Link
        // action-card__image-wrapper
        itemProp='url'
        href={href || ['', item.city.url, 'actions', item.slug, ''].join('/')}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        css={{
          height: 0,
          width: '100%',
          paddingBottom: '75%',
          position: 'relative',
          display: 'block',
          '&:hover': {
            color: theme.colors.black,
          },
        }}
      >
        {item.image && (
          <div
            // action-card__image
            css={{
              borderRadius: '4px',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              overflow: 'hidden',
            }}
          >
            <Picture
              initWhiteBlackFilter={
                item.not_season || item.status === 'archive'
              }
              src={item.image}
              alt={item.title}
              title={item.title_short}
              is360
            />
          </div>
        )}
        {item.countdown_type &&
          item.countdown_type !== 'unset' &&
          timerData.showTimer && (
            <div
              css={{
                width: '100%',
                background: 'rgba(0, 0, 0, 0.7)',
                color: '#fff',
                padding: '6px 0',
                zIndex: 10,
                position: 'absolute',
                left: 0,
                bottom: 0,
              }}
            >
              <div
                css={{
                  width: '90%',
                  margin: '0 auto',
                }}
              >
                <p css={{ textAlign: 'center', fontSize: 13 }}>
                  {timerData.title} {formatTimer(days, hours, minutes, seconds)}
                </p>
                <div
                  css={{
                    width: '100%',
                    height: 3,
                    background: '#fff',
                    borderRadius: 2,
                    marginTop: 5,
                  }}
                >
                  <div
                    css={{
                      height: '100%',
                      width: `${progressValue}%`,
                      background: 'rgb(244, 67, 54)',
                      borderRadius: 2,
                      marginLeft: -1,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
      </Link>
      <ul
        // action-card__label-container-sub
        css={{
          padding: 0,
          margin: 0,
          position: 'absolute',
          top: '-7px',
          left: '8px',
          right: '8px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
        }}
      >
        {item.not_season && (
          <LabelFeatureListItem text='Не сезон' title='Не сезон' />
        )}
        {item.best_reviews && (
          <LabelFeatureListItem
            text='лучшие отзывы'
            title='Пользователи высоко оценивают мероприятие'
          />
        )}
        {item.hit && (
          <LabelFeatureListItem
            text='хит'
            title='Пользователи часто просматривают это мероприятие'
          />
        )}
        {item.first_time && (
          <LabelFeatureListItem
            text='впервые'
            title='Событие размещено на сайте впервые'
          />
        )}
        {item.premiere && (
          <LabelFeatureListItem
            text='премьера'
            title='Премьерные показы мероприятия'
          />
        )}
        {item.prepayment && (
          <LabelFeatureListItem
            text='предоплата'
            title='Онлайн вы вносите только предоплату'
          />
        )}
      </ul>
      <FavoriteButtonCardWrapper>
        <FavoriteButton
          kind={FavoriteKinds.event}
          id={item.id}
          size='small'
          variant='red-outline'
        />
      </FavoriteButtonCardWrapper>
      <div
        // action-card__label-block
        css={{
          fontFamily: 'Golos',
          position: 'absolute',
          top: '30px',
          left: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {!item.instead_price &&
          !item.filter_price &&
          item.type !== eventTypes.restaurant && (
            <ActionLabelItem
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              css={{
                borderRadius: item.pushkin_card_payment ? '0' : '0 4px 4px 0',
                backgroundColor: theme.colors.red,
                background: 'rgba(0, 102, 204, 0.80)',
              }}
            >
              бесплатно
            </ActionLabelItem>
          )}
        {!!item.discount && (
          <ActionLabelItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            css={{
              borderRadius: item.pushkin_card_payment ? '0' : '0 4px 4px 0',
              backgroundColor: 'rgba(244, 67, 54, 0.80)',
            }}
          >
            −{item.discount}%&nbsp;скидка
          </ActionLabelItem>
        )}
        {item.pushkin_card_payment && (
          <div
            css={{
              width: 34,
              height: 34,
              background: '#fff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              opacity: 0.8,
            }}
          >
            <PushkinSVG css={{ width: 24, height: 24 }} />
          </div>
        )}
      </div>
      <div
        // action-card__info
        css={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '20px 20px 40px',
        }}
      >
        <div
          // action-card__description
          css={{
            color: '#000',
            fontFamily: 'Golos',
            fontWeight: 400,
            fontSize: '12px',
            '&>div': {
              marginBottom: '6px',
            },
          }}
        >
          {item.filter_price && item.type !== eventTypes.restaurant ? (
            <DescriptionItem
              itemProp='offers'
              itemScope
              itemType='https://schema.org/Offer'
            >
              <RedMoneySVG css={{ width: '16px', height: '16px' }} />
              {!!item.instead_price &&
                (!!item.filter_price || !!item.price_amount) && (
                  <span
                    title={`Ваша экономия ${getPriceWithCurrency(
                      getBenefit(
                        item.instead_price,
                        item.type === 'tsnext_widget' ||
                          item.type === 'partner_widget'
                          ? Number(item.price_amount)
                          : Number(item.filter_price)
                      ),
                      item.price_currency
                    )}`}
                  >
                    <span
                      css={{
                        color: theme.colors.textSecondary,
                        opacity: '0.6',
                        textDecoration: 'line-through',
                      }}
                    >
                      {splitPriceByThree(item.instead_price)}&nbsp;
                    </span>{' '}
                    <span itemProp='price'>
                      {getPriceWithCurrency(
                        item.type === 'tsnext_widget' ||
                          item.type === 'partner_widget'
                          ? Number(item.price_amount)
                          : Number(item.filter_price),
                        item.price_currency
                      )}
                    </span>
                    <meta
                      itemProp='priceCurrency'
                      content={item.price_currency}
                    />
                  </span>
                )}
              {!!item.filter_price && !item.instead_price && (
                <span>
                  {`от ${getPriceWithCurrency(
                    item.type === 'tsnext_widget' ||
                      item.type === 'partner_widget'
                      ? Number(item.price_amount)
                      : Number(item.filter_price),
                    item.price_currency
                  )}`}
                </span>
              )}
            </DescriptionItem>
          ) : (
            ''
          )}
          {item.type !== eventTypes.restaurant && !!item.dates_txt && (
            <DescriptionItem
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              css={{
                '& span:not(:last-child):after': {
                  content: '", "',
                },
              }}
            >
              {item.has_further_dates && item.dates_txt ? (
                <>
                  <WhenSVG css={{ width: '16px', height: '16px' }} />
                  <span>{item.dates_txt}</span>
                </>
              ) : (
                <span
                  css={{
                    display: 'flex',
                    gap: 8,
                  }}
                >
                  {item.date_unknown && (
                    <>
                      <WhenSVG css={{ width: '16px', height: '16px' }} />
                      <p>Дата уточняется</p>
                    </>
                  )}
                </span>
              )}
            </DescriptionItem>
          )}
          {item.place_title && (
            <DescriptionItem itemProp='location'>
              <LocationSVG
                fill={theme.colors.red}
                css={{ width: '16px', height: '16px' }}
              />
              <span itemProp='name'>
                {showOnlyCityInAddress ? item.city.title : item.place_title}
              </span>
            </DescriptionItem>
          )}
        </div>
        <Link
          // action-card__title
          itemProp='url'
          href={href || ['', item.city.url, 'actions', item.slug, ''].join('/')}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          css={{
            fontFamily: 'Montserrat',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '22px',
            letterSpacing: '0em',
            color: theme.colors.black,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            '&:hover': {
              color: theme.colors.black,
            },
          }}
        >
          <span itemProp='name'>{item.title}</span>
        </Link>
        <div
          css={{
            position: 'absolute',
            bottom: '12px',
            right: '20px',
            left: '20px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            color: theme.colors.grayStrong,
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '18px',
              height: '16px',
              fontFamily: 'Golos',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '0.01em',
              svg: {
                flex: '0 0 auto',
                marginRight: '5px',
              },
            }}
          >
            <ViewsSVG
              fill={theme.colors.grayStrong}
              css={{ width: '16px', height: '16px' }}
            />
            <span>{item.views}</span>
          </div>
          {item.comments_avg_rating > 0 && (
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                marginRight: '18px',
                height: '16px',
                fontFamily: 'Golos',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: '0.01em',
                svg: {
                  flex: '0 0 auto',
                  marginRight: '5px',
                },
              }}
            >
              <RatingSVG css={{ width: '16px', height: '16px' }} />
              <span>{item.comments_avg_rating.toFixed(1)}</span>
            </div>
          )}
          {item.age_category && (
            <div
              css={{
                fontFamily: 'Golos',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: '0.01em',
                marginLeft: 'auto',
                color: theme.colors.grayStrong,
              }}
            >
              {item.age_category}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div
      // actions__item actions__item_scroll
      itemScope
      itemType='https://schema.org/Event'
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      css={{
        ...theme.mixins.col(4),
        marginBottom: 16,
        [DEVICE_SCREENS_MAX_STRING.xl]: {
          ...theme.mixins.col(3),
        },
        [DEVICE_SCREENS_MAX_STRING.md]: {
          ...theme.mixins.col(2),
        },
        ...(scroll
          ? {
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                ...theme.mixins.col(1.3),
                marginBottom: 0,
              },
            }
          : {}),
        ...(catalog
          ? {
              ...theme.mixins.col(3),
              [DEVICE_SCREENS_MAX_STRING.md]: {
                ...theme.mixins.col(2),
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                ...theme.mixins.col(1),
              },
            }
          : {}),
      }}
      className='actions__item'
    >
      {item.nearest_event && item.nearest_event.start_date && (
        <meta itemProp='startDate' content={item.nearest_event.start_date} />
      )}
      {item.nearest_event && item.nearest_event.end_date && (
        <meta itemProp='startDate' content={item.nearest_event.end_date} />
      )}
      <meta itemProp='name' content={item.title} />
      <div css={{ display: 'flex', height: '100%' }}>
        <div
          // action-card
          css={{
            height: '100%',
            borderRadius: '4px',
            backgroundColor: '#fff',
            position: 'relative',
            top: 0,
            width: '100%',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.constants.boxShadow,
            },
          }}
        >
          <Link
            // action-card__image-wrapper
            itemProp='url'
            href={
              href || ['', item.city.url, 'actions', item.slug, ''].join('/')
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            css={{
              height: 0,
              width: '100%',
              paddingBottom: '75%',
              position: 'relative',
              display: 'block',
              '&:hover': {
                color: theme.colors.black,
              },
            }}
          >
            {(item.photo_preview || item.image) && (
              <div
                // action-card__image
                css={{
                  borderRadius: '4px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  overflow: 'hidden',
                }}
              >
                <Picture
                  initWhiteBlackFilter={
                    item.not_season || item.status === 'archive'
                  }
                  src={item.photo_preview || (item.image as ImageSources)}
                  alt={item.title}
                  title={item.title_short}
                  is360
                />
              </div>
            )}

            {item.countdown_type &&
              item.countdown_type !== 'unset' &&
              timerData.showTimer && (
                <div
                  css={{
                    width: '100%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    padding: '6px 0',
                    zIndex: 10,
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <div
                    css={{
                      width: '90%',
                      margin: '0 auto',
                    }}
                  >
                    <p css={{ textAlign: 'center', fontSize: 13 }}>
                      {timerData.title}{' '}
                      {formatTimer(days, hours, minutes, seconds)}
                    </p>
                    <div
                      css={{
                        width: '100%',
                        height: 3,
                        background: '#fff',
                        borderRadius: 2,
                        marginTop: 5,
                      }}
                    >
                      <div
                        css={{
                          height: '100%',
                          width: `${progressValue}%`,
                          background: 'rgb(244, 67, 54)',
                          borderRadius: 2,
                          marginLeft: -1,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
          </Link>
          <ul
            // action-card__label-container-sub
            css={{
              padding: 0,
              margin: 0,
              position: 'absolute',
              top: '-7px',
              left: '8px',
              right: '8px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            {item.not_season && (
              <LabelFeatureListItem text='Не сезон' title='Не сезон' />
            )}
            {item.best_reviews && (
              <LabelFeatureListItem
                text='лучшие отзывы'
                title='Пользователи высоко оценивают мероприятие'
              />
            )}
            {item.hit && (
              <LabelFeatureListItem
                text='хит'
                title='Пользователи часто просматривают это мероприятие'
              />
            )}
            {item.first_time && (
              <LabelFeatureListItem
                text='впервые'
                title='Событие размещено на сайте впервые'
              />
            )}
            {item.premiere && (
              <LabelFeatureListItem
                text='премьера'
                title='Премьерные показы мероприятия'
              />
            )}
            {item.prepayment && (
              <LabelFeatureListItem
                text='предоплата'
                title='Онлайн вы вносите только предоплату'
              />
            )}
          </ul>
          <FavoriteButtonCardWrapper>
            <FavoriteButton
              kind={FavoriteKinds.event}
              id={item.id}
              size='small'
              variant='red-outline'
            />
          </FavoriteButtonCardWrapper>
          <div
            // action-card__label-block

            css={{
              fontFamily: 'Golos',
              position: 'absolute',
              top: '30px',
              left: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {!item.instead_price &&
              !item.filter_price &&
              item.type !== eventTypes.restaurant && (
                <ActionLabelItem
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  css={{
                    borderRadius: item.pushkin_card_payment
                      ? '0'
                      : '0 4px 4px 0',
                    backgroundColor: theme.colors.red,
                    background: 'rgba(0, 102, 204, 0.80)',
                  }}
                >
                  бесплатно
                </ActionLabelItem>
              )}
            {!!item.discount && (
              <ActionLabelItem
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                css={{
                  borderRadius: item.pushkin_card_payment ? '0' : '0 4px 4px 0',
                  backgroundColor: 'rgba(244, 67, 54, 0.80)',
                }}
              >
                −{item.discount}%&nbsp;скидка
              </ActionLabelItem>
            )}
            {item.pushkin_card_payment && (
              <div
                css={{
                  width: 34,
                  height: 34,
                  background: '#fff',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  opacity: 0.8,
                }}
              >
                <PushkinSVG css={{ width: 24, height: 24 }} />
              </div>
            )}
          </div>
          <div
            // action-card__info
            css={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '20px 20px 40px',
            }}
          >
            <div
              // action-card__description
              css={{
                color: '#000',
                fontFamily: 'Golos',
                fontWeight: 400,
                fontSize: '12px',
                '&>div': {
                  marginBottom: '6px',
                },
              }}
            >
              {item.filter_price && item.type !== eventTypes.restaurant ? (
                <DescriptionItem
                  itemProp='offers'
                  itemScope
                  itemType='https://schema.org/Offer'
                >
                  <RedMoneySVG css={{ width: '16px', height: '16px' }} />
                  {!!item.instead_price &&
                    (!!item.filter_price || !!item.price_amount) && (
                      <span
                        title={`Ваша экономия ${getPriceWithCurrency(
                          getBenefit(
                            item.instead_price,
                            item.type === 'tsnext_widget' ||
                              item.type === 'partner_widget'
                              ? Number(item.price_amount)
                              : Number(item.filter_price)
                          ),
                          item.price_currency
                        )}`}
                      >
                        <span
                          css={{
                            color: theme.colors.textSecondary,
                            opacity: '0.6',
                            textDecoration: 'line-through',
                          }}
                        >
                          {splitPriceByThree(item.instead_price)}&nbsp;
                        </span>{' '}
                        <span itemProp='price'>
                          {getPriceWithCurrency(
                            item.type === 'tsnext_widget' ||
                              item.type === 'partner_widget'
                              ? Number(item.price_amount)
                              : Number(item.filter_price),
                            item.price_currency
                          )}
                        </span>
                        <meta
                          itemProp='priceCurrency'
                          content={item.price_currency}
                        />
                      </span>
                    )}
                  {(!!item.filter_price || !!item.price_amount) &&
                    !item.instead_price && (
                      <span>
                        {`от ${getPriceWithCurrency(
                          item.type === 'partner_widget' ||
                            item.type === 'tsnext_widget'
                            ? Number(item.price_amount)
                            : Number(item.filter_price),
                          item.price_currency
                        )}`}
                      </span>
                    )}
                </DescriptionItem>
              ) : (
                ''
              )}
              {item.type !== eventTypes.restaurant && !!item.dates_txt && (
                <DescriptionItem
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  css={{
                    '& span:not(:last-child):after': {
                      content: '", "',
                    },
                  }}
                >
                  {item.has_further_dates && item.dates_txt ? (
                    <>
                      <WhenSVG css={{ width: '16px', height: '16px' }} />
                      <span>{item.dates_txt}</span>
                    </>
                  ) : (
                    <span
                      css={{
                        display: 'flex',
                        gap: 8,
                      }}
                    >
                      {item.date_unknown && (
                        <>
                          <WhenSVG css={{ width: '16px', height: '16px' }} />
                          <p>Дата уточняется</p>
                        </>
                      )}
                    </span>
                  )}
                </DescriptionItem>
              )}
              {item.place_title && (
                <DescriptionItem itemProp='location'>
                  <LocationSVG
                    fill={theme.colors.red}
                    css={{ width: '16px', height: '16px' }}
                  />
                  <span itemProp='name'>
                    {showOnlyCityInAddress ? item.city.title : item.place_title}
                  </span>
                </DescriptionItem>
              )}
            </div>
            <Link
              // action-card__title
              itemProp='url'
              href={
                href || ['', item.city.url, 'actions', item.slug, ''].join('/')
              }
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              css={{
                fontFamily: 'Montserrat',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '22px',
                letterSpacing: '0em',
                color: theme.colors.black,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                '&:hover': {
                  color: theme.colors.black,
                },
              }}
            >
              <span itemProp='name'>{item.title}</span>
            </Link>
            <div
              css={{
                position: 'absolute',
                bottom: '12px',
                right: '20px',
                left: '20px',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: theme.colors.grayStrong,
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '18px',
                  height: '16px',
                  fontFamily: 'Golos',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '16px',
                  letterSpacing: '0.01em',
                  svg: {
                    flex: '0 0 auto',
                    marginRight: '5px',
                  },
                }}
              >
                <ViewsSVG
                  fill={theme.colors.grayStrong}
                  css={{ width: '16px', height: '16px' }}
                />
                <span>{item.views}</span>
              </div>
              {item.comments_avg_rating > 0 && (
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '18px',
                    height: '16px',
                    fontFamily: 'Golos',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '16px',
                    letterSpacing: '0.01em',
                    svg: {
                      flex: '0 0 auto',
                      marginRight: '5px',
                    },
                  }}
                >
                  <RatingSVG css={{ width: '16px', height: '16px' }} />
                  <span>{item.comments_avg_rating.toFixed(1)}</span>
                </div>
              )}
              {item.age_category && (
                <div
                  css={{
                    fontFamily: 'Golos',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '16px',
                    letterSpacing: '0.01em',
                    marginLeft: 'auto',
                    color: theme.colors.grayStrong,
                  }}
                >
                  {item.age_category}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
